import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NativeTapToPay } from 'src/app/plugins/native-runtime.plugins';

/**
 * Supplies the official Tap to Pay SF Symbol from the native iOS bridge.
 *
 * The local SVG is only a browser/legacy-binary fallback. Native iOS builds
 * receive a rasterized `wave.3.right.circle.fill` from UIKit so the webview
 * never substitutes an Apple logo or a third-party artwork asset.
 */
@Injectable({ providedIn: 'root' })
export class TapToPayBrandingService {
  public readonly symbolName = 'wave.3.right.circle.fill';
  public readonly fallbackImage = 'assets/icon/tap-to-pay.svg';

  private cachedImage: string | null = null;
  private pendingImage: Promise<string> | null = null;

  public async getSymbolImage(): Promise<string> {
    if (this.cachedImage) {
      return this.cachedImage;
    }

    if (Capacitor.getPlatform() !== 'ios') {
      return this.fallbackImage;
    }

    if (this.pendingImage) {
      return this.pendingImage;
    }

    this.pendingImage = (async () => {
      try {
        const branding = await NativeTapToPay.branding();
        if (branding?.imageDataUrl) {
          this.cachedImage = branding.imageDataUrl;
          return branding.imageDataUrl;
        }
      } catch (error) {
        // Keep the UI usable with an older installed binary that does not yet
        // expose the branding method.
        console.warn('[Tap to Pay] Native SF Symbol branding unavailable.', error);
      }

      return this.fallbackImage;
    })();

    try {
      return await this.pendingImage;
    } finally {
      this.pendingImage = null;
    }
  }
}
